import type { CandidateArticle } from "../../../../../api/types"
import type { BrowserStatement } from "./ArticleBrowser"
import type { SearchSuggestion } from "./suggestions"

export interface BrowserCounts {
  /** Suggestion hit counts, aligned by index with the `suggestions` array
   *  passed in. */
  suggestions: number[]
  /** Statement hit counts, keyed by `BrowserStatement.id`. */
  statements: Map<string, number>
}

/**
 * One pass over `base` counting, for every quick view, how many of its
 * articles match: the numbers shown beside each row in the browser's left
 * rail (`suggestionCount`/`statementCount` used to be closures re-created
 * every render and re-run over the whole pool once per row; for a 41k-article
 * org that is O(views * pool) on every keystroke). Callers memoise the
 * result themselves, keyed on `base`/`suggestions`/`statements`.
 */
export function buildCounts(
  base: CandidateArticle[],
  suggestions: SearchSuggestion[],
  statements: BrowserStatement[],
  matchesAny: (a: CandidateArticle, needles: string[]) => boolean,
): BrowserCounts {
  const suggestionNeedles = suggestions.map((s) =>
    s.terms.map((t) => t.toLowerCase()),
  )
  const suggestionSets = suggestions.map((s) =>
    s.articleNumbers ? new Set(s.articleNumbers) : null,
  )
  const statementSets = statements.map((s) => new Set(s.articleNumbers))
  const suggestionCounts = new Array<number>(suggestionNeedles.length).fill(0)
  const statementCounts = new Array<number>(statementSets.length).fill(0)

  for (const a of base) {
    for (let i = 0; i < suggestionNeedles.length; i++) {
      const set = suggestionSets[i]
      if (set ? set.has(a.articleNumber) : matchesAny(a, suggestionNeedles[i]))
        suggestionCounts[i]++
    }
    for (let i = 0; i < statementSets.length; i++) {
      if (statementSets[i].has(a.articleNumber)) statementCounts[i]++
    }
  }

  const statementMap = new Map<string, number>()
  statements.forEach((s, i) => statementMap.set(s.id, statementCounts[i]))
  return { suggestions: suggestionCounts, statements: statementMap }
}
